/**
 * SKU编辑页面 Hooks - 自动保存
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { App } from 'antd';
import type { FormInstance } from 'antd';
import { skuAPI } from '../../../services/api';
import type { SKU } from '../../../types/core-types';

export type SaveStatus = 'saved' | 'saving' | 'error' | 'idle';

export const useAutoSave = (id: string | undefined, form: FormInstance) => {
    const { message } = App.useApp();
    const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');
    const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const formChangedRef = useRef(false);

    // 自动保存函数
    const autoSave = useCallback(async (values: Partial<SKU>) => {
        if (!id) return;

        try {
            setSaveStatus('saving');
            // 确保ID是数字
            const skuId = Number(id);
            if (isNaN(skuId)) {
                console.error('无效的SKU ID:', id);
                setSaveStatus('error');
                return;
            }

            console.log('正在自动保存:', values);
            await skuAPI.update(skuId, values);
            setSaveStatus('saved');
            formChangedRef.current = false;

            // 2秒后重置状态
            setTimeout(() => {
                setSaveStatus('idle');
            }, 2000);
        } catch (error: any) {
            setSaveStatus('error');
            // 获取详细错误信息
            const errorMsg = error.response?.data?.detail || error.message || '未知错误';
            console.error('自动保存失败:', errorMsg, error);
            // 只有手动保存才提示错误，自动保存失败只记录日志，避免打扰用户
        }
    }, [id]);

    // 表单变化处理（防抖）
    const handleFormChange = useCallback(() => {
        formChangedRef.current = true;
        setSaveStatus('idle'); // 重置状态为idle，让用户知道有更改未保存

        if (saveTimeoutRef.current) {
            clearTimeout(saveTimeoutRef.current);
        }

        // 2秒后自动保存（增加防抖时间）
        saveTimeoutRef.current = setTimeout(() => {
            form.validateFields()
                .then((values) => {
                    autoSave(values);
                })
                .catch((err) => {
                    console.warn('表单验证失败，跳过自动保存:', err);
                });
        }, 2000);
    }, [form, autoSave]);

    // 手动保存
    const handleManualSave = async () => {
        try {
            const values = await form.validateFields();
            await autoSave(values);
            message.success('保存成功');
        } catch (error) {
            console.error('手动保存失败:', error);
            message.error('保存失败，请检查表单填写是否正确');
        }
    };

    // 清理定时器
    useEffect(() => {
        return () => {
            if (saveTimeoutRef.current) {
                clearTimeout(saveTimeoutRef.current);
            }
        };
    }, []);

    return {
        saveStatus,
        formChanged: formChangedRef.current,
        handleFormChange,
        handleManualSave,
    };
};
